/**
 * Singleton binding seam for the global upload-queue enqueue facade.
 *
 * The 3 upload wires (`features/create-project`, `features/upload-project-file`,
 * `features/manage-project-artifacts`) need to enqueue uploads into the
 * `features/upload-queue` store, but per FSD the `features/X → features/Y`
 * value edge is forbidden. This binding seam inverts the dependency: the
 * composition root (`main.tsx`) calls `configureUploadQueueBindings()` once
 * at app boot, providing a stable enqueue function from
 * `@/features/upload-queue`. The wires then call `enqueueUpload(payload)`
 * from this module WITHOUT importing `@/features/upload-queue` directly.
 *
 * Pattern mirrors `lib/chat-actions/chat-actions-bindings.ts`.
 *
 * The `EnqueueUploadFn` shape matches what `enqueueUploadImpl()` returns
 * (per call: id, abortSignal, onProgress, markSuccess, markFailed). The
 * binding function is a plain function (NOT a React hook) so it works
 * inside non-component code such as the wizard's `uploadSingleFile` and
 * the library/folder mutation callbacks.
 *
 * The payload type is imported as `EnqueueInput` from
 * `@/features/upload-queue` rather than redeclared here — the feature owns
 * the data shape, so adding a new UploadSource cannot leave two definitions
 * drifting apart with no compile-time guard. `EnqueueUploadHandle` below is
 * the mirror of that on the return side: the impl imports it from here
 * instead of declaring its own.
 */

import type { EnqueueInput } from '@/features/upload-queue';

export interface EnqueueUploadHandle {
  id: string;
  abortSignal: AbortSignal;
  onProgress: (loaded: number, total: number) => void;
  markSuccess: () => void;
  markFailed: (error: string) => void;
}

export type EnqueueUploadFn = (payload: EnqueueInput) => EnqueueUploadHandle;

export interface UploadQueueBindings {
  enqueueUpload: EnqueueUploadFn;
}

let bindings: UploadQueueBindings | null = null;

/**
 * Configures the upload-queue bindings. Called once at app boot from the
 * composition root. Safe to call multiple times — subsequent calls replace
 * the bindings (tests use `configureUploadQueueBindingsForTests`).
 */
export const configureUploadQueueBindings = (next: UploadQueueBindings): void => {
  bindings = next;
};

/**
 * Test-only reset. Re-applies the bindings or clears them. Production
 * code MUST NOT call this — use `configureUploadQueueBindings` once at
 * boot.
 */
export const configureUploadQueueBindingsForTests = (next: UploadQueueBindings | null): void => {
  bindings = next;
};

/**
 * Enqueue a new upload into the global queue. Returns the per-call control
 * surface (id, abortSignal, onProgress, markSuccess, markFailed). Throws
 * fail-fast if called before `configureUploadQueueBindings()` runs at app
 * boot — that would indicate a wiring bug.
 */
export const enqueueUpload: EnqueueUploadFn = (payload) => {
  if (bindings === null) {
    throw new Error(
      '[lib/upload-queue] Upload-queue bindings not configured. ' +
        'Call `configureUploadQueueBindings` at app boot before any ' +
        'consumer invokes `enqueueUpload()`.',
    );
  }
  return bindings.enqueueUpload(payload);
};
